import { useNavigate } from "react-router-dom";
import { useState, useMemo, useRef, useEffect } from "react";
import { ClipLoader } from "react-spinners";

function normalize(str) {
    return (str ?? "")
        .toString()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLowerCase()
        .trim();
}

export default function RWATable({ rwas = [] }) {
    const navigate = useNavigate();

    // Search
    const [searchQuery, setSearchQuery] = useState("");
    const [showSearch, setShowSearch] = useState(false);
    const searchRef = useRef(null);
    const searchWrapperRef = useRef(null);

    const handleSearchVisible = () => {
        setShowSearch((prev) => {
            const next = !prev;
            if (next && searchRef.current) searchRef.current.focus();
            return next;
        });
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
                setShowSearch(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const dateFmt = useMemo(
        () =>
            new Intl.DateTimeFormat("es-ES", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            }),
        []
    );

    // Filtrar por name
    const filteredRwas = useMemo(() => {
        const q = normalize(searchQuery);
        if (!q) return rwas;
        return rwas.filter((rwa) => normalize(rwa?.name ?? "").includes(q));
    }, [rwas, searchQuery]);

    // Ordenar por updatedAt tras filtrar
    const sortedRwas = useMemo(() => {
        return [...filteredRwas].sort(
            (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
    }, [filteredRwas]);

    return (
        <div className="">
            {/* Header con título y buscador */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold">All Assets</h2>
                    <p className="text-slate-500">
                        View and manage your tokenized real-world assets.
                    </p>
                </div>

                <div className="relative" ref={searchWrapperRef}>
                    <input
                        ref={searchRef}
                        type="text"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setShowSearch(true)}
                        className={`pl-10 pr-4 py-[6px] h-[32px] bg-gray-100 rounded-full text-sm ${showSearch ? "w-72" : "w-32"
									} outline-none transition-all duration-300`}
                        aria-label="Search assets by name"
                    />
                    <img
                        src="/icons/search.svg"
                        alt="Search"
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 cursor-pointer opacity-50"
                        onClick={handleSearchVisible}
                    />
                </div>
            </div>

            {/* Tabla */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <table className="w-full text-left border-collapse table-fixed">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[28%]">
                                Asset ID
                            </th>
                            <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[28%]">
                                Name
                            </th>
                            <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[16%]">
                                Type
                            </th>
                            <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[14%]">
                                Created
                            </th>
                            <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[14%]">
                                Last Edited
                            </th>
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                        {sortedRwas.map((rwa) => {
                            const deploying = !rwa.smartContractAddress;
                            return (
                            <tr
                                key={rwa.id}
                                className={`transition-colors ${deploying ? "opacity-50 cursor-default" : "hover:bg-slate-50/50 cursor-pointer"}`}
                                onClick={() => !deploying && navigate(`/dashboard/rwa/${rwa.id}`)}
                                tabIndex={deploying ? -1 : 0}
                                onKeyDown={(e) => {
                                    if (!deploying && e.key === "Enter") navigate(`/dashboard/rwa/${rwa.id}`);
                                }}
                                role="button"
                                aria-label={`Open asset ${rwa.name || rwa.id}`}
                            >
                                <td className="px-6 py-4">
                                    <span className="font-mono text-xs text-slate-600 truncate block">{rwa.id}</span>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="font-semibold text-sm text-slate-900 truncate">{rwa.name}</span>
                                        {deploying && <ClipLoader size={14} color="#94a3b8" />}
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <span className="capitalize text-sm text-slate-700 px-2 py-1 bg-slate-100 rounded-md truncate block">
                                        {rwa.type}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <span className="text-sm text-slate-700 truncate block">
                                        {dateFmt.format(new Date(rwa.createdAt))}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <span className="text-sm text-slate-700 truncate block">
                                        {dateFmt.format(new Date(rwa.updatedAt))}
                                    </span>
                                </td>
                            </tr>
                            );
                        })}

                        {sortedRwas.length === 0 && (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="py-8 px-6 text-center text-slate-400 text-sm"
                                >
                                    No RWAs found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
